import useDebounce from 'hooks/useDebounce';
import { FC, useEffect, useState } from 'react';
import { Controller, ControllerRenderProps } from 'react-hook-form';
import { ActionMeta, MultiValue } from 'react-select';
import { getExtendedSuggestions } from 'services';
import { ExtendedSuggestion, SettlementShort } from 'types';
import { Select } from 'ui/redesign/Select';
import { OptionType } from 'ui/redesign/Select/types';
import { SettlementsSelectProps } from './types';
import { ProfileService } from 'services/ProfileService';
import { toast } from 'react-toastify';

const notFoundCityMessage = 'Город/населённый пункт не найден';

export const CitiesSelect: FC<SettlementsSelectProps> = ({
  control,
  isMulti = false,
  name,
  label,
  onChange,
  isFormDisabled,
  clearErrors,
  errorMessage,
  ...rest
}) => {
  const [settlements, setSettlements] = useState<ExtendedSuggestion[]>([]);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    getCities('');
    return () => {
      setIsLoading(false);
    };
  }, []);

  const getCities = async (template: string) => {
    setIsLoading(true);
    const response = await getExtendedSuggestions(template, 1);
    if (response.status === 200) {
      setSettlements(response.data);
    }
    setIsLoading(false);
  };

  const debouncedSettlements = useDebounce(getCities, 500);

  const getUniqueCities = (
    citiesArr: ExtendedSuggestion[],
    valueArr?: OptionType<SettlementShort>[]
  ) => {
    let uniqueCities = citiesArr;

    valueArr &&
      valueArr?.length > 0 &&
      valueArr?.forEach((value) => {
        uniqueCities = uniqueCities.filter(
          (city) => city.suggestion.data.fias_id !== value.value.fiasId
        );
      });

    return uniqueCities;
  };

  const transformSuggestionToSelectOption = (array: ExtendedSuggestion[]) => {
    return Array.isArray(array)
      ? array.map((item) => ({
          label: item.suggestion.value,
          value: item,
          isDisabled: item.isMarked,
        }))
      : [];
  };

  const removeNotFoundCity = (
    value: MultiValue<OptionType<ExtendedSuggestion | SettlementShort>>,
    removedOptionValue: OptionType<ExtendedSuggestion>
  ) =>
    value.filter((optionValue) => {
      const value = optionValue.value as ExtendedSuggestion;
      return !(
        value.suggestion &&
        value.suggestion.data.fias_id === removedOptionValue.value.suggestion.data.fias_id
      );
    });

  const handleSuggestionRequestError = (
    field: ControllerRenderProps<any, string>,
    value: MultiValue<OptionType<ExtendedSuggestion | SettlementShort>>,
    newOptionValue: OptionType<ExtendedSuggestion>,
    message: string
  ) => {
    toast.error(message);
    field.onChange(
      removeNotFoundCity(
        value as MultiValue<OptionType<ExtendedSuggestion | SettlementShort>>,
        newOptionValue
      )
    );
    onChange && onChange(value);
  };

  const changeHandler = async (
    value:
      | MultiValue<OptionType<SettlementShort | ExtendedSuggestion>>
      | OptionType<SettlementShort>
      | null,
    meta: ActionMeta<OptionType>,
    field: ControllerRenderProps<any, string>
  ) => {
    field.onChange(value);
    if (value) {
      clearErrors && clearErrors(name);
    } else {
      onChange && onChange(value);
    }

    if (isMulti) {
      const newOptionValue = meta.option as OptionType<ExtendedSuggestion> | undefined;
      if (
        newOptionValue &&
        value?.find((optionValue: OptionType<ExtendedSuggestion | SettlementShort>) => {
          const value = optionValue.value as ExtendedSuggestion;
          return (
            value.suggestion &&
            value.suggestion.data.fias_id === newOptionValue.value.suggestion.data.fias_id
          );
        }) !== undefined
      ) {
        try {
          const response = await ProfileService.getUserSettlementBySuggestion(newOptionValue.value);
          if (response.data.isSuccess) {
            const actualOptionValue = {
              label: response.data.commonSettlement.objectFullName,
              value: response.data.commonSettlement,
            };
            const newValue: MultiValue<OptionType<SettlementShort>> | null = value?.map(
              (optionValue: OptionType<ExtendedSuggestion | SettlementShort>) => {
                const value = optionValue.value as ExtendedSuggestion;
                return value.suggestion &&
                  value.suggestion.data.fias_id === actualOptionValue.value.fiasId
                  ? actualOptionValue
                  : optionValue;
              }
            );
            if (newValue) {
              field.onChange(newValue);
              onChange && onChange(newValue);
            }
          } else {
            handleSuggestionRequestError(
              field,
              value as MultiValue<OptionType<ExtendedSuggestion | SettlementShort>>,
              newOptionValue,
              response.data.message
            );
          }
        } catch (error) {
          handleSuggestionRequestError(
            field,
            value as MultiValue<OptionType<ExtendedSuggestion | SettlementShort>>,
            newOptionValue,
            notFoundCityMessage
          );
        }
      } else {
        onChange && onChange(value as MultiValue<OptionType<SettlementShort>>, meta);
      }
    } else {
      if (value) {
        try {
          const response = await ProfileService.getUserSettlementBySuggestion(
            (value as unknown as OptionType<ExtendedSuggestion>).value
          );

          if (response.data.isSuccess) {
            const actualOptionValue = {
              label: response.data.commonSettlement.objectFullName,
              value: response.data.commonSettlement,
            };
            if (response.data.commonSettlement) {
              field.onChange(actualOptionValue);
              onChange && onChange(actualOptionValue);
            }
          } else {
            toast.error(response.data.message);
            field.onChange(null);
            onChange && onChange(null);
          }
        } catch (error) {
          toast.error(notFoundCityMessage);
          field.onChange(null);
          onChange && onChange(null);
        }
      }
    }
  };

  return (
    <div className="field-select w-full max-w-[640px] px-[1px]">
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          return (
            <Select
              name={name}
              variant="cities"
              value={field.value}
              label={label}
              isMulti={isMulti}
              isDisabled={isFormDisabled}
              onChange={(value, meta) => changeHandler(value, meta, field)}
              errorMessage={errorMessage}
              options={transformSuggestionToSelectOption(getUniqueCities(settlements, field.value))}
              isLoading={isLoading}
              onInputChange={(newValue: string) => {
                if (newValue.length < 3) {
                  return;
                }
                debouncedSettlements(newValue);
              }}
              {...rest}
            />
          );
        }}
      />
    </div>
  );
};
